import { useCallback, useEffect, useRef } from 'react';

/**
 * Applies the noise gate from localStorage to a MediaStream's audio tracks.
 * When the RMS volume is below the threshold the tracks are disabled (no transmission).
 * The gate stays open for `holdMs` after the last above-threshold sample so
 * natural pauses in speech don't cut out mid-sentence.
 *
 * Reads `voip_noise_gate` (0–100) and `voip_noise_gate_hold` (ms) from localStorage.
 * A gate value of 0 means off — tracks are always enabled.
 */
export function useNoiseGate(streamRef) {
  const cleanupRef = useRef(null);

  const stop = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    // Re-enable tracks so they're not stuck muted after the gate stops
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = true; });
  }, [streamRef]);

  const start = useCallback((stream) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    const gate = parseFloat(localStorage.getItem('voip_noise_gate') ?? '0');
    const holdMs = parseFloat(localStorage.getItem('voip_noise_gate_hold') ?? '300');

    if (gate === 0) return; // gate is off

    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    const buf = new Float32Array(analyser.fftSize);
    let lastAboveAt = 0;
    let animFrame;

    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      const level = Math.min(100, rms * 400);

      // Re-read prefs each tick so mid-call setting changes take effect immediately
      const currentGate = parseFloat(localStorage.getItem('voip_noise_gate') ?? '0');
      const currentHold = parseFloat(localStorage.getItem('voip_noise_gate_hold') ?? '300');

      const now = performance.now();
      const isAbove = currentGate > 0 && level >= currentGate;
      if (isAbove) lastAboveAt = now;
      const open = currentGate === 0 || isAbove || (now - lastAboveAt) < currentHold;

      stream.getAudioTracks().forEach((t) => {
        // Don't fight the user's manual mute — only gate if track is currently enabled
        if (t.enabled !== open && (open || t.enabled)) {
          t.enabled = open;
        }
      });

      animFrame = requestAnimationFrame(tick);
    };
    tick();

    cleanupRef.current = () => {
      cancelAnimationFrame(animFrame);
      ctx.close();
    };
  }, [stop]);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { startGate: start, stopGate: stop };
}
